// Used by: Home, Blog, BlogArticle, Ambassadors, LandingPage, Careers, ModalCopyQuoteBooking
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import useModal from "../../hooks/useModal";
import usePost from "../../hooks/usePost";
import useQuoteQueryScroll from "../../hooks/useQuoteQueryScroll";
import QuoteQueryPersonalDetails from "./QuoteQueryPersonalDetails";
import QuoteQueryJourneyRow from "./QuoteQueryJourneyRow";
import getClassName from "../../utilities/getClassName";
import { formatTimeTo24h } from "../../utilities/date";
import {
    createInitialJourney,
    getInitialJourneyDataForTripType,
    getLegIconName,
    formatJourneyForSubmit,
    syncReturnLegs,
    transformJourneysForTripTypeChange,
} from "../../utilities/quoteQueryJets";
import authContext from "../../store/AuthContext";
import poweredByContext from "../../store/PoweredByContext";
import useGeoLocation from "react-ipgeolocation";
import { getIcon, getRawIcon } from "../../utilities/icon";
import TripTypeTabs from "./TripTypeTabs";

import plusDarkIcon from "../../assets/icons/plus-dark.svg";
import exclamationIcon from "../../assets/icons/exclamation-mark.svg";

const QuoteQueryJets = ({
    onFocus,
    inModal,
    onPage,
    closeQuoteModal,
    onModalSuccess,
    updateSending,
    quoteOrBookingToCopy,
    skeleton,
    tripType: propTripType,
    setTripType: propSetTripType,
}) => {
    const [internalTripType, setInternalTripType] = useState("oneway");
    const tripType = propTripType ?? internalTripType;
    const setTripType = propSetTripType ?? setInternalTripType;
    const [journeyCounter, setJourneyCounter] = useState(() => (tripType === "oneway" ? 1 : 2));
    const [journeyData, setJourneyData] = useState(() => getInitialJourneyDataForTripType(tripType));
    // Tracks the trip type the current journeyData was built for, so we can
    // transform (rather than wipe) the legs when the trip type changes.
    const prevTripTypeRef = useRef(tripType);
    const [validateForm, setValidateForm] = useState(false);
    const [formIsValid, setFormIsValid] = useState(false);
    const [showError, setShowError] = useState(false);
    const [requests, setRequests] = useState("");
    const [resetFormRef, setResetFormRef] = useState(0);
    const [openDropdownId, setOpenDropdownId] = useState(null);

    const quoteQueryRef = useRef();
    const quoteQueryMarkerRef = useRef();
    const quoteQueryMarkerRef2 = useRef();

    const { isScrolled, mobileIsActive, quoteQueryMarkerHeight, toggleForMobile, checkOverflow } = useQuoteQueryScroll(
        quoteQueryRef,
        inModal
    );

    const { modalVisible, openModal, closeModal } = useModal();
    const { sending, returnMessage, postData } = usePost();
    const location = useGeoLocation();
    const authCtx = useContext(authContext);
    const poweredByCtx = useContext(poweredByContext);

    const getTime = useCallback(
        (time) => (location?.country === "US" ? formatTimeTo24h(time) : time),
        [location?.country]
    );

    const clearValidationState = useCallback(() => {
        setValidateForm(false);
        setFormIsValid(false);
        setShowError(false);
    }, []);

    const validateFormHandler = useCallback(() => {
        let errorCount = 0;

        journeyData.forEach((journey) => {
            let dateIsInPast = false;
            if (journey.date) {
                const dateParts = journey.date.split("/");
                const date = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
                const today = new Date();
                today.setHours(0, 0, -1);
                dateIsInPast = date < today;
            }
            if (!journey.flightFrom) errorCount++;
            if (!journey.flightTo) errorCount++;
            if (!journey.date || dateIsInPast) errorCount++;
            if (!journey.time) errorCount++;
        });

        setFormIsValid(errorCount === 0);
        if (errorCount === 0) setShowError(false);
    }, [journeyData]);

    useEffect(() => {
        validateFormHandler();
    }, [journeyData, validateForm, validateFormHandler]);

    // The tabs only set the trip type; the effect below reacts to the change
    // (this also covers the trip type being controlled by a parent page).
    const handleTripTypeChange = useCallback(
        (type) => {
            setTripType?.(type);
        },
        [setTripType]
    );

    // When the trip type changes, transform the existing legs instead of
    // clearing them. Bumping resetFormRef remounts the inputs so they re-seed
    // (and re-select) from the preserved journey data.
    useEffect(() => {
        const prevType = prevTripTypeRef.current;
        if (prevType === tripType) return;
        prevTripTypeRef.current = tripType;

        const { journeys, counter } = transformJourneysForTripTypeChange(
            prevType,
            tripType,
            journeyData,
            journeyCounter
        );
        setJourneyData(journeys);
        setJourneyCounter(counter);
        clearValidationState();
        setResetFormRef((r) => r + 1);
    }, [tripType, journeyData, journeyCounter, clearValidationState]);

    useEffect(() => {
        if (returnMessage.success) onModalSuccess?.();
    }, [returnMessage, onModalSuccess]);

    useEffect(() => {
        if (typeof updateSending === "function") updateSending(sending);
    }, [sending, updateSending]);

    useEffect(() => {
        if (!quoteOrBookingToCopy?.legs?.length) return;
        const legs = quoteOrBookingToCopy.legs;
        const toJourney = (leg, i) => {
            const [m, d, y] = (leg.departureDate || "").split("/");
            const dateStr = m && d && y ? `${d}/${m}/${y}` : "";
            return createInitialJourney(i + 1, {
                flightFrom: leg.flightFromLong
                    ? { name: leg.flightFromLong, city: "", icao: leg.flightFromIcao }
                    : undefined,
                flightTo: leg.flightToLong ? { name: leg.flightToLong, city: "", icao: leg.flightToIcao } : undefined,
                date: dateStr,
                time: leg.departureTime ?? "",
                passengers: quoteOrBookingToCopy.passengers ?? 1,
                luggage: quoteOrBookingToCopy.luggage ?? 1,
                carryOn: quoteOrBookingToCopy.carryOn ?? quoteOrBookingToCopy.itemsOfCarryOn ?? 1,
            });
        };
        setJourneyCounter(legs.length);
        setJourneyData(legs.map(toJourney));
    }, [quoteOrBookingToCopy]);

    const addJourneyHandler = useCallback(() => {
        const lastJourney = journeyData[journeyData.length - 1];
        const newId = journeyCounter + 1;
        setJourneyCounter(newId);
        const newJourney = createInitialJourney(newId, {
            ...(tripType === "returns"
                ? {
                      flightFrom: lastJourney.flightTo,
                      flightTo: lastJourney.flightFrom,
                  }
                : {}),
            date: "",
            time: lastJourney.time,
            luggage: lastJourney.luggage,
            carryOn: lastJourney.carryOn,
            passengers: lastJourney.passengers,
        });
        setJourneyData((prev) => [...prev, newJourney]);
        clearValidationState();
    }, [journeyData, journeyCounter, tripType, clearValidationState]);

    const removeJourneyHandler = useCallback((index) => {
        setJourneyData((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const resetJourneyHandler = useCallback(() => {
        clearValidationState();
        setJourneyCounter(1);
        window.scrollTo(0, 0);
        setResetFormRef((r) => r + 1);
        setJourneyData([createInitialJourney(1)]);
    }, [clearValidationState]);

    const updateInputHandler = useCallback(
        (index, key, value) => {
            setJourneyData((prev) => {
                const next = [...prev];
                next[index] = { ...next[index], [key]: value };

                if (tripType === "returns") {
                    return syncReturnLegs(next, index, key);
                }

                return next;
            });
        },
        [tripType]
    );

    const getCopiedProperty = useCallback(
        (property, leg) => {
            if (!quoteOrBookingToCopy) return undefined;
            let dataSource = quoteOrBookingToCopy;
            let isNewLeg = true;

            if (typeof leg === "number" && quoteOrBookingToCopy.legs) {
                if (quoteOrBookingToCopy.legs.length > leg) {
                    dataSource = quoteOrBookingToCopy.legs[leg];
                    isNewLeg = false;
                } else if (quoteOrBookingToCopy.legs.length === 0 && leg === 0) {
                    isNewLeg = false;
                }
            }

            if (isNewLeg) return undefined;

            switch (property) {
                case "flightFrom":
                    return {
                        name: dataSource.flightFromLong,
                        city: "",
                        icao: dataSource.flightFromIcao,
                    };
                case "flightTo":
                    return {
                        name: dataSource.flightToLong,
                        city: "",
                        icao: dataSource.flightToIcao,
                    };
                case "departureDate": {
                    const parts = dataSource.departureDate.split("/");
                    return new Date(parts[2], parts[0] - 1, parts[1]);
                }
                case "departureTime": {
                    const [h, m] = dataSource.departureTime.split(":");
                    const d = new Date();
                    d.setHours(parseInt(h, 10), parseInt(m, 10), 0);
                    return d;
                }
                case "passengers": {
                    const v = quoteOrBookingToCopy.passengers;
                    return { label: v, value: v };
                }
                case "luggage": {
                    const v = quoteOrBookingToCopy.luggage;
                    return { label: v, value: v };
                }
                default:
                    return undefined;
            }
        },
        [quoteOrBookingToCopy]
    );

    const getCopiedDate = useCallback(
        (i) => {
            const copied = getCopiedProperty("departureDate", i);
            if (copied) return copied;
            const journey = journeyData[i];
            if (!journey?.date) return undefined;
            const [d, m, y] = journey.date.split("/");
            return new Date(y, m - 1, d);
        },
        [getCopiedProperty, journeyData]
    );

    const submitHandler = useCallback(
        (e) => {
            e.preventDefault();
            setValidateForm(true);

            if (inModal) {
                const formatted = journeyData.map((flight) => formatJourneyForSubmit(flight, getTime));
                if (authCtx.token && formIsValid) {
                    postData(
                        {
                            journeys: formatted,
                            id: authCtx.id,
                            additionalRequests: requests,
                            tenantID: poweredByCtx.tenantID,
                        },
                        "/api/flight/search-flights-by-member",
                        1000
                    );
                }
            } else {
                if (formIsValid) openModal();
                else setShowError(true);
            }
        },
        [
            inModal,
            journeyData,
            getTime,
            authCtx.token,
            authCtx.id,
            formIsValid,
            requests,
            poweredByCtx.tenantID,
            postData,
            openModal,
        ]
    );

    const getModalCloseButtonColor = () =>
        poweredByCtx.isPoweredBy && poweredByCtx.tenant?.isDarkQuoteModalHeader ? "black" : "white";

    const addLegIconName = getLegIconName("add-leg", {
        onPage,
        isScrolled,
        isPoweredBy: poweredByCtx?.isPoweredBy,
    });

    const rootClasses = getClassName([
        "quote-query",
        inModal ? "quote-query--in-modal" : "",
        onPage ? "quote-query--on-page" : "",
        isScrolled ? "quote-query--scrolled" : "",
        isScrolled && tripType === "oneway" ? "quote-query--scrolled-oneway" : "",
        isScrolled && tripType === "returns" ? "quote-query--scrolled-returns" : "",
        isScrolled && tripType === "multilegs" ? "quote-query--scrolled-multilegs" : "",
        !isScrolled && tripType === "oneway" ? "quote-query--oneway" : "",
        !isScrolled && tripType === "returns" ? "quote-query--returns" : "",
        !isScrolled && tripType === "multilegs" ? "quote-query--multilegs" : "",
        checkOverflow() ? "quote-query--scrolled-overflow" : "",
        skeleton ? "quote-query--skeleton" : "",
    ]);

    const contentClasses = getClassName([
        "quote-query__content",
        isScrolled ? "wrap" : "",
        mobileIsActive ? "quote-query__content--active" : "",
    ]);

    return (
        <>
            <div ref={quoteQueryMarkerRef2} className="quote-query-marker" style={{ position: "relative", top: -56 }} />
            <div ref={quoteQueryMarkerRef} className="quote-query-marker" style={{ height: quoteQueryMarkerHeight }} />
            <div ref={quoteQueryRef} className={rootClasses}>
                {inModal && returnMessage.success ? (
                    <QuoteQueryPersonalDetails
                        journeyData={journeyData}
                        resetJourney={resetJourneyHandler}
                        journeyIsValid={formIsValid}
                        closeModal={closeQuoteModal}
                        inModal={inModal}
                    />
                ) : (
                    <div className={contentClasses}>
                        {mobileIsActive && <div className="quote-query__sticky-title">Private Jets</div>}
                        {isScrolled && (
                            <TripTypeTabs
                                tripType={tripType}
                                onTripTypeChange={handleTripTypeChange}
                                className="quote-query__trip-type-tabs"
                            />
                        )}
                        <form onSubmit={submitHandler}>
                            {!isScrolled && onPage && (
                                <TripTypeTabs
                                    tripType={tripType}
                                    onTripTypeChange={handleTripTypeChange}
                                    className="quote-query__trip-type-tabs"
                                />
                            )}
                            {journeyData.map((journey, i) => (
                                <QuoteQueryJourneyRow
                                    key={journey.id}
                                    journey={journey}
                                    index={i}
                                    journeyData={journeyData}
                                    tripType={tripType}
                                    resetFormRef={resetFormRef}
                                    validateForm={validateForm}
                                    onFocus={onFocus}
                                    skeleton={skeleton}
                                    inModal={inModal}
                                    updateInputHandler={updateInputHandler}
                                    removeJourneyHandler={removeJourneyHandler}
                                    getCopiedProperty={getCopiedProperty}
                                    getCopiedDate={getCopiedDate}
                                    setRequests={setRequests}
                                    openDropdownId={openDropdownId}
                                    setOpenDropdownId={setOpenDropdownId}
                                    location={location}
                                    poweredByCtx={poweredByCtx}
                                    onPage={onPage}
                                    isScrolled={isScrolled}
                                />
                            ))}
                        </form>
                        <div className="quote-query__actions">
                            {tripType === "multilegs" ? (
                                <button
                                    type="button"
                                    className="quote-query__actions__add-leg-button"
                                    onClick={addJourneyHandler}
                                >
                                    {inModal ? (
                                        <img alt="" src={plusDarkIcon} />
                                    ) : poweredByCtx?.isPoweredBy ? (
                                        <>{getRawIcon("add-leg")}</>
                                    ) : (
                                        <img alt="" src={getIcon(addLegIconName)} />
                                    )}
                                    Add leg
                                </button>
                            ) : (
                                <div className="quote-query__actions__placeholder" />
                            )}
                            {showError && (
                                <div className="quote-query__error">
                                    <img src={exclamationIcon} alt="" />
                                    Please complete your journey details.
                                </div>
                            )}
                            <div className="quote-query__submit-container">
                                <Button onClick={submitHandler} classNames={["button--full"]}>
                                    {inModal ? "Submit quote" : "Get a Quote"}
                                </Button>
                                {inModal && (
                                    <Button
                                        onClick={closeQuoteModal}
                                        classNames={["button--secondary", "button--light-border", "button--full"]}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {isScrolled && (
                    <Button
                        classNames={["quote-query__expand-button", "quote-query__expand-button--mobile"]}
                        onClick={toggleForMobile}
                    >
                        {poweredByCtx?.isPoweredBy ? (
                            getRawIcon("chevron-down-sticky-quote")
                        ) : (
                            <img src={getIcon("chevron-down-sticky-quote")} alt="" />
                        )}
                        {mobileIsActive ? "Close quote" : "Start a quote"}
                    </Button>
                )}
            </div>
            {!inModal && (
                <Modal
                    modalVisible={modalVisible}
                    closeCrossColour={getModalCloseButtonColor()}
                    closeModal={closeModal}
                    classNames={["modal__content--quote-personal-details"]}
                >
                    <QuoteQueryPersonalDetails
                        is24Hour={location?.country !== "US"}
                        journeyData={journeyData}
                        resetJourney={resetJourneyHandler}
                        journeyIsValid={formIsValid}
                        closeModal={closeModal}
                    />
                </Modal>
            )}
        </>
    );
};

export default QuoteQueryJets;
